import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import type {
  StripeCardCvcElementChangeEvent,
  StripeCardCvcElementOptions,
  StripeCardExpiryElementChangeEvent,
  StripeCardExpiryElementOptions,
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
  StripeCardNumberElementOptions,
} from "@stripe/stripe-js";
import { checkoutCopy } from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";

const CARD_ELEMENT_STYLE: NonNullable<StripeCardNumberElementOptions["style"]> = {
  base: {
    color: "#404040",
    iconColor: "#EE4B9A",
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: "500",
    fontSize: "16px",
    letterSpacing: "0.025em",
    "::placeholder": {
      color: "#9ca3af",
    },
  },
  invalid: {
    color: "#c41f1f",
    iconColor: "#c41f1f",
  },
};

const CARD_NUMBER_OPTIONS: StripeCardNumberElementOptions = {
  style: CARD_ELEMENT_STYLE,
  placeholder: checkoutCopy.payment.stripe.cardNumberPlaceholder,
};

const CARD_EXPIRY_OPTIONS: StripeCardExpiryElementOptions = {
  style: CARD_ELEMENT_STYLE,
};

const CARD_CVC_OPTIONS: StripeCardCvcElementOptions = {
  style: CARD_ELEMENT_STYLE,
};

export interface StripePaymentController {
  createPaymentMethod(): Promise<{ paymentMethodId?: string; error?: string }>;
}

export interface StripePaymentStatus {
  isComplete: boolean;
  hasError: boolean;
}

interface StripePaymentFieldsProps {
  externalError: string | null;
  onReady: (controller: StripePaymentController | null) => void;
  onStatusChange: (status: StripePaymentStatus) => void;
}

type CompletionState = {
  number: boolean;
  expiry: boolean;
  cvc: boolean;
};

type ErrorState = {
  number: string | null;
  expiry: string | null;
  cvc: string | null;
};

const INITIAL_COMPLETION: CompletionState = { number: false, expiry: false, cvc: false };
const INITIAL_ERRORS: ErrorState = { number: null, expiry: null, cvc: null };

const collectErrorMessage = (errors: ErrorState, externalError: string | null): string | null => {
  return externalError ?? errors.number ?? errors.expiry ?? errors.cvc;
};

const StripeCardFields = ({ externalError, onReady, onStatusChange }: StripePaymentFieldsProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [completion, setCompletion] = useState<CompletionState>(INITIAL_COMPLETION);
  const [errors, setErrors] = useState<ErrorState>(INITIAL_ERRORS);

  useEffect(() => {
    setCompletion(INITIAL_COMPLETION);
    setErrors(INITIAL_ERRORS);
  }, [elements, stripe]);

  useEffect(() => {
    const nextCompletion = completion;
    const nextErrors = errors;
    const errorMessage = collectErrorMessage(nextErrors, externalError);
    const isComplete = nextCompletion.number && nextCompletion.expiry && nextCompletion.cvc;
    onStatusChange({ isComplete, hasError: Boolean(errorMessage) });
  }, [completion, errors, externalError, onStatusChange]);

  useEffect(() => {
    if (!stripe || !elements) {
      onReady(null);
      return;
    }

    const controller: StripePaymentController = {
      async createPaymentMethod() {
        const cardNumber = elements.getElement(CardNumberElement) as StripeCardNumberElement | null;

        if (!cardNumber) {
          return { error: checkoutCopy.payment.stripe.notReady };
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({ type: "card", card: cardNumber });

        if (error) {
          return { error: error.message ?? checkoutCopy.payment.stripe.tokenizationError };
        }

        if (!paymentMethod?.id) {
          return { error: checkoutCopy.payment.stripe.tokenizationError };
        }

        return { paymentMethodId: paymentMethod.id };
      },
    };

    onReady(controller);

    return () => {
      onReady(null);
    };
  }, [elements, onReady, stripe]);

  const handleNumberChange = useCallback((event: StripeCardNumberElementChangeEvent) => {
    setCompletion((prev) => ({ ...prev, number: event.complete }));
    setErrors((prev) => ({ ...prev, number: event.error?.message ?? null }));
  }, []);

  const handleExpiryChange = useCallback((event: StripeCardExpiryElementChangeEvent) => {
    setCompletion((prev) => ({ ...prev, expiry: event.complete }));
    setErrors((prev) => ({ ...prev, expiry: event.error?.message ?? null }));
  }, []);

  const handleCvcChange = useCallback((event: StripeCardCvcElementChangeEvent) => {
    setCompletion((prev) => ({ ...prev, cvc: event.complete }));
    setErrors((prev) => ({ ...prev, cvc: event.error?.message ?? null }));
  }, []);

  const cardNumberFieldId = useId();
  const cardExpiryFieldId = useId();
  const cardCvcFieldId = useId();

  const errorMessage = collectErrorMessage(errors, externalError);

  return (
    <section className={styles.stripeSection} aria-labelledby="stripe-card-details-title">
      <h3 id="stripe-card-details-title" className={styles.paymentFormTitle}>
        {checkoutCopy.payment.stripe.heading}
      </h3>
      <div className={styles.stripeFields}>
        <div className={styles.stripeFieldGroup}>
          <label htmlFor={cardNumberFieldId} className={styles.paymentFieldLabel}>
            {checkoutCopy.payment.stripe.cardNumberLabel}
          </label>
          <div className={styles.stripeInput}>
            <CardNumberElement
              id={cardNumberFieldId}
              options={CARD_NUMBER_OPTIONS}
              onChange={handleNumberChange}
              className={styles.stripeElement}
            />
          </div>
        </div>
        <div className={styles.stripeSplitRow}>
          <div className={styles.stripeFieldGroup}>
            <label htmlFor={cardExpiryFieldId} className={styles.paymentFieldLabel}>
              {checkoutCopy.payment.stripe.expiryLabel}
            </label>
            <div className={styles.stripeInput}>
              <CardExpiryElement
                id={cardExpiryFieldId}
                options={CARD_EXPIRY_OPTIONS}
                onChange={handleExpiryChange}
                className={styles.stripeElement}
              />
            </div>
          </div>
          <div className={styles.stripeFieldGroup}>
            <label htmlFor={cardCvcFieldId} className={styles.paymentFieldLabel}>
              {checkoutCopy.payment.stripe.cvcLabel}
            </label>
            <div className={styles.stripeInput}>
              <CardCvcElement
                id={cardCvcFieldId}
                options={CARD_CVC_OPTIONS}
                onChange={handleCvcChange}
                className={styles.stripeElement}
              />
            </div>
          </div>
        </div>
      </div>
      {errorMessage && (
        <p className={styles.stripeError} role="alert">
          {errorMessage}
        </p>
      )}
    </section>
  );
};

export const StripePaymentFields = ({ externalError, onReady, onStatusChange }: StripePaymentFieldsProps) => {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  useEffect(() => {
    if (!publishableKey) {
      onReady(null);
      onStatusChange({ isComplete: false, hasError: true });
    }
  }, [onReady, onStatusChange, publishableKey]);

  if (!publishableKey || !stripePromise) {
    return (
      <section className={styles.stripeSection} aria-labelledby="stripe-card-details-title">
        <h3 id="stripe-card-details-title" className={styles.paymentFormTitle}>
          {checkoutCopy.payment.stripe.heading}
        </h3>
        <p className={styles.stripeError} role="alert">
          {checkoutCopy.payment.stripe.missingKey}
        </p>
      </section>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ appearance: { theme: "flat" } }}>
      <StripeCardFields externalError={externalError} onReady={onReady} onStatusChange={onStatusChange} />
    </Elements>
  );
};
